import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { Button, Image } from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";

const getPreviewFromFile = (file) => {
    if (!file || typeof file === "string") return file || null;
    if (file instanceof File) return URL.createObjectURL(file);
    return null;
};

const CheckInImageUpload = ({ imageObj, handleFileSelect, layout = "square", emptyIcon, t }) => {
    const isAddDamage = imageObj.create;
    const [preview, setPreview] = useState(() => getPreviewFromFile(imageObj.uploadedImageFile));

    useEffect(() => {
        if (isAddDamage) return undefined;

        const nextPreview = getPreviewFromFile(imageObj.uploadedImageFile);
        setPreview((current) => {
            if (current?.startsWith?.("blob:") && current !== nextPreview) {
                URL.revokeObjectURL(current);
            }
            return nextPreview;
        });

        return () => undefined;
    }, [imageObj.id, imageObj.uploadedImageFile, isAddDamage]);

    useEffect(
        () => () => {
            if (preview?.startsWith?.("blob:")) {
                URL.revokeObjectURL(preview);
            }
        },
        [preview],
    );

    const handleFileChange = (event) => {
        const input = event.target;
        const file = input.files?.[0];
        if (!file) return;

        if (!imageObj.create) {
            setPreview((current) => {
                if (current?.startsWith?.("blob:")) {
                    URL.revokeObjectURL(current);
                }
                return URL.createObjectURL(file);
            });
        }

        handleFileSelect({ ...imageObj, uploadedImageFile: file });
        input.value = "";
    };

    const handleRemove = (event) => {
        event.preventDefault();
        event.stopPropagation();

        if (preview?.startsWith?.("blob:")) {
            URL.revokeObjectURL(preview);
        }

        setPreview(null);
        handleFileSelect({ ...imageObj, uploadedImageFile: "" });
    };

    const tileClassName =
        layout === "wide"
            ? "relative flex h-[148px] w-full cursor-pointer flex-col items-center justify-center rounded-2xl transition-colors md:h-[168px]"
            : "relative flex aspect-square w-full cursor-pointer flex-col items-center justify-center rounded-2xl transition-colors";

    if (isAddDamage) {
        return (
            <label className="mt-4 inline-flex cursor-pointer items-center gap-2 rounded-full border border-dashed border-[#D0D5DD] bg-white px-4 py-2.5 text-sm font-medium text-[#101828] transition-colors hover:border-[#5D5DF6]">
                <input type="file" accept="image/*" className="hidden" onChange={handleFileChange} />
                <span className="flex h-5 w-5 items-center justify-center rounded-full border border-[#D0D5DD] text-[10px] text-[#667085]">
                    <PlusOutlined />
                </span>
                {t("addDamagePhoto")}
            </label>
        );
    }

    return (
        <label
            className={`${tileClassName} ${
                preview
                    ? "border-2 border-solid border-[#12B76A]"
                    : "border-2 border-dashed border-[#D0D5DD] bg-white hover:border-[#5D5DF6]"
            }`}>
            <input type="file" accept="image/*" className="hidden" onChange={handleFileChange} />
            {preview ? (
                <>
                    <Image
                        src={preview}
                        alt={imageObj.name}
                        width="100%"
                        height="100%"
                        preview={false}
                        wrapperClassName="pointer-events-none !block !h-full !w-full overflow-hidden rounded-[14px]"
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                    <Button
                        type="text"
                        danger
                        shape="circle"
                        size="small"
                        icon={<DeleteOutlined />}
                        aria-label={t("removePhoto")}
                        className="!absolute !right-2 !top-2 z-[2] !m-0 !bg-white shadow-sm"
                        onClick={handleRemove}
                        onMouseDown={(event) => event.stopPropagation()}
                    />
                </>
            ) : (
                <div className="flex flex-col items-center justify-center gap-1.5 px-3">
                    {emptyIcon}
                    {imageObj.number ? (
                        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#E4E7EC] text-xs font-medium text-[#667085]">
                            {imageObj.number}
                        </span>
                    ) : null}
                    <span className="text-sm font-semibold text-[#101828]">{imageObj.name}</span>
                    <span className="text-sm text-[#5D5DF6]">{t("tapToCapture")}</span>
                </div>
            )}
        </label>
    );
};

CheckInImageUpload.propTypes = {
    imageObj: PropTypes.object.isRequired,
    handleFileSelect: PropTypes.func.isRequired,
    layout: PropTypes.string,
    emptyIcon: PropTypes.node,
    t: PropTypes.func.isRequired,
};

export default CheckInImageUpload;
